import { useMemo, useRef } from 'react'
import * as echarts from 'components/charts/echarts'
import { EChartsOption, useECharts } from 'components/charts/useECharts'
import { getChartData } from 'components/Events/EventCard/helpers/getChartData'
import { IMPERIAL_UNIT_SYSTEM, useTimeZone, useUnitSystem } from 'context'
import { getUnixTime } from 'date-fns'
import { first, last, sortBy } from 'lodash-es'
import { theme } from 'theme'
import { TWELVE_HOURS_TIME_FORMAT } from 'utility/timeFormats'
import { IEntryGlucose } from 'types'

const GRAPH_HEIGHT = 140

const LAST_MEASUREMENT_KEY = 'lastMeasurement'
const PEAK_MEASUREMENT_KEY = 'peakMeasurement'
const FIRST_MEASUREMENT_KEY = 'firstMeasurement'
const DUAL_MEASUREMENT_KEY = 'dualMeasurementKey'

const POINTS_SIZE = {
  [PEAK_MEASUREMENT_KEY]: 12,
  [FIRST_MEASUREMENT_KEY]: 8,
  [LAST_MEASUREMENT_KEY]: 8,
  [DUAL_MEASUREMENT_KEY]: 12
}

const POINT_PEAK_COLOR = 'rgb(254, 64, 113)'
const POINT_BORDER_COLOR = 'black'
const LINE_COLOR = theme.palette.graph['transparent-brand-primary']

const POINTS_STYLES = {
  [PEAK_MEASUREMENT_KEY]: { color: POINT_PEAK_COLOR, borderColor: POINT_BORDER_COLOR },
  [FIRST_MEASUREMENT_KEY]: { color: LINE_COLOR, borderColor: POINT_BORDER_COLOR },
  [LAST_MEASUREMENT_KEY]: { color: LINE_COLOR, borderColor: POINT_BORDER_COLOR },
  [DUAL_MEASUREMENT_KEY]: { color: LINE_COLOR, borderColor: POINT_PEAK_COLOR, borderWidth: 2 }
}

type MeasurementKey = keyof typeof POINTS_SIZE

export const TwoHourGraph = ({
  meal
}: {
  meal:
    | { occurredAt: string }
    | {
        glucose?: IEntryGlucose | null
        occurredAt: string
      }
}) => {
  const { unitSystem } = useUnitSystem()
  const { formatWithTimeZone } = useTimeZone()
  const containerRef = useRef<HTMLDivElement>(null)

  const isImperial = unitSystem === IMPERIAL_UNIT_SYSTEM
  const precision = isImperial ? 0 : 1

  const { activityData, centeredChartData, startTimeTick, endTimeTick } = getChartData(meal)

  const pointsData = activityData.filter((point) => !point.fake)

  const firstPoint = first(pointsData)
  if (firstPoint) firstPoint.name = FIRST_MEASUREMENT_KEY

  const lastMeasurementPoint = last(pointsData)
  if (lastMeasurementPoint) lastMeasurementPoint.name = LAST_MEASUREMENT_KEY

  const peakMeasurementPoint = useMemo(() => last(sortBy(pointsData, 'y')), [pointsData])

  if (peakMeasurementPoint) {
    // sometimes the peak data point can be the same as the first (if the first is the highest)
    // or last (if the last is the highest) measurement.
    // In these cases, we use the DUAL_MEASUREMENT_KEY name to identify it and shows
    // a custom bullet with specific styles.
    peakMeasurementPoint.name = !peakMeasurementPoint.name
      ? PEAK_MEASUREMENT_KEY
      : DUAL_MEASUREMENT_KEY
  }

  const chartData = centeredChartData.map((value) => ({ ...value, x: getUnixTime(value.x) }))

  const option = useMemo<EChartsOption>(() => {
    const xValues = chartData.map((point) => point.x)
    const xMin = Math.min(...xValues)
    const xMax = Math.max(...xValues)
    const xSpan = xMax - xMin || 1
    const activityStartOffset = Math.min(1, Math.max(0, (startTimeTick.x - xMin) / xSpan))
    const activityEndOffset = Math.min(1, Math.max(0, (endTimeTick.x - xMin) / xSpan))
    const areaColor = new echarts.graphic.LinearGradient(0, 0, 1, 0, [
      { offset: 0, color: 'white' },
      { offset: activityStartOffset, color: 'white' },
      { offset: activityStartOffset, color: LINE_COLOR },
      { offset: activityEndOffset, color: LINE_COLOR },
      { offset: activityEndOffset, color: 'white' },
      { offset: 1, color: 'white' }
    ])

    const pointSeriesData = chartData
      .filter((point): point is typeof point & { name: MeasurementKey } => Boolean(point.name))
      .map((point) => ({
        value: [point.x, point.y],
        symbolSize: POINTS_SIZE[point.name],
        itemStyle: POINTS_STYLES[point.name],
        label: {
          show: true,
          position: 'top' as const,
          fontWeight: 'bold' as const,
          formatter: point.displayValue ?? point.y.toFixed(precision)
        }
      }))

    return {
      animation: false,
      grid: { left: 12, right: 12, top: 30, bottom: 30 },
      xAxis: { type: 'value', show: false, scale: true },
      yAxis: { type: 'value', show: false, scale: true },
      series: [
        {
          type: 'line',
          showSymbol: false,
          smooth: false,
          data: chartData.map((point) => [point.x, point.y]),
          lineStyle: { color: LINE_COLOR, opacity: 1 },
          areaStyle: { color: areaColor, opacity: 0.2 },
          markLine: {
            silent: true,
            symbol: 'none',
            lineStyle: { color: LINE_COLOR, opacity: 1 },
            label: {
              show: true,
              position: 'start',
              fontSize: 12,
              formatter: (params) =>
                formatWithTimeZone(
                  (params as unknown as { value: number }).value * 1000,
                  TWELVE_HOURS_TIME_FORMAT
                )
            },
            data: [{ xAxis: startTimeTick.x }, { xAxis: endTimeTick.x }]
          }
        },
        {
          type: 'scatter',
          z: 10,
          itemStyle: { opacity: 1 },
          data: pointSeriesData
        }
      ]
    }
  }, [chartData, precision, startTimeTick.x, endTimeTick.x, formatWithTimeZone])

  useECharts(containerRef, option)

  return chartData.length > 0 ? (
    <div ref={containerRef} style={{ width: '100%', height: GRAPH_HEIGHT }} />
  ) : null
}
